import React, { useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import IncidentDetailsStyles from "./IncidentDetailsStyles";

interface PhotoUploadProps {
  photos: File[];
  onPhotosChange: (photos: File[]) => void;
  maxPhotos?: number;
  title?: string;
  type: "vehicle" | "property";
  
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  photos,
  onPhotosChange,
  maxPhotos = 5,
  title = "Upload Photos",
  type,
  
}) => {
  const [photoError, setPhotoError] = useState<string>("");

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length + photos.length > maxPhotos) {
      setPhotoError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }
    setPhotoError("");
    onPhotosChange([...photos, ...files]);
  };

  const handleRemovePhoto = (index: number) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    onPhotosChange(newPhotos);
    setPhotoError("");
  };

  return (
    <Box
      sx={IncidentDetailsStyles.uploadBox}
    >
      <input
        accept="image/*"
        style={IncidentDetailsStyles.uploadInput}
        id={`photo-upload-${type}`}
        multiple
        type="file"
        onChange={handlePhotoUpload}
      />
      <label htmlFor={`photo-upload-${type}`}>
        <Button
          id={`photo-upload-${type}`}
          data-test-id={`test-photo-upload-${type}`}
          aria-label={`photo-upload-${type}`}
          variant="outlined"
          component="span"
          startIcon={<AddPhotoAlternateIcon />}
          sx={IncidentDetailsStyles.uploadButton}
        >
          {title}
        </Button>
      </label>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={IncidentDetailsStyles.uploadCaption}
      >
        Upload up to {maxPhotos} photos.
      </Typography>
      {photoError && (
        <Typography
          variant="caption"
          color="error"
          sx={IncidentDetailsStyles.errorText}
        >
          {photoError}
        </Typography>
      )}
      {photos.length > 0 && (
        <Box sx={IncidentDetailsStyles.uploadedPhotosBox}>
          <Typography
            variant="body2"
            sx={IncidentDetailsStyles.uploadedPhotosText}
          >
            {photos.length} photo(s) uploaded
          </Typography>
          <Box sx={IncidentDetailsStyles.uploadedPhotosList}>
            {photos.map((photo, index) => (
              <Box
                key={index}
                sx={{
                  width: 120, 
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  m: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row", 
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <img
                     src={URL.createObjectURL(photo)}
                     alt={`Photo ${index + 1}`}
                     style={{
                       width: 80,
                       height: 80,
                       objectFit: "cover",
                       display: "block",
                     }}
                  />
                  <Box
                  sx={{display: "flex",flexDirection: "column", alignItems: "center",
                  }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: "white", fontWeight: 500, }}
                    >
                      {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemovePhoto(index)}
                      sx={{ mt: "-4rem" }}
                      id={`photo-delete-${type}`}
                      data-test-id={`test-photo-delete-${type}`}
                      aria-label={`photo-delete-${type}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PhotoUpload;
